import React from 'react';
import { Search } from 'lucide-react';
import { App } from '../../types';
import { useAccessibleApps, useIsSystemAdmin, ApplicationType } from '../../services/queries/permissionQueries';
import { shouldBypassRoleChecks } from '../../config/mvpConfig';
import { StandardInput } from '../standard';

interface AppLauncherProps {
  apps: App[];
  currentApp?: string;
  onAppSelect: (app: App) => void;
  onClose: () => void;
}

const AppLauncher: React.FC<AppLauncherProps> = ({ apps, currentApp, onAppSelect, onClose }) => {
  const { apps: userAccessibleApps } = useAccessibleApps();
  const { isSystemAdmin } = useIsSystemAdmin();
  const bypassRoles = shouldBypassRoleChecks();

  const regalMainApp = {
    name: 'Regal (main view)',
    description: 'Return to main CRM dashboard and client management',
    icon: '🏠',
    color: 'bg-gray-600'
  };

  const getAppType = (appName: string): ApplicationType | 'system_admin' => {
    const lowerName = appName.toLowerCase();
    if (lowerName.includes('system') && lowerName.includes('admin')) return 'system_admin';
    if (lowerName.includes('cybersweep')) return 'cybersweep';
    if (lowerName.includes('credit block')) return 'credit_block';
    if (lowerName.includes('credit management')) return 'credit_management';
    return 'regal_main';
  };

  const accessibleApps = apps.filter(app => {
    const appType = getAppType(app.name);
    if (appType === 'system_admin') return bypassRoles || isSystemAdmin;
    if (bypassRoles) return true;
    return userAccessibleApps.includes(appType);
  });

  // Filter out the current app from the list and add Regal main if not already there
  const availableApps = currentApp && currentApp !== 'home'
    ? [regalMainApp, ...accessibleApps.filter(app => app.name.toLowerCase().replace(' ', '-') !== currentApp.replace('app-', ''))]
    : accessibleApps;

  return (
    <div className="absolute top-16 left-0 w-80 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-600 z-50">
      <div className="p-4 border-b border-gray-200 dark:border-gray-600">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400 dark:text-gray-500" />
          <StandardInput
            type="text"
            placeholder="Search apps..."
            className="w-full pl-10 text-sm"
          />
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Application Launcher</h3>
        <div className="space-y-2">
          {availableApps.map((app, index) => (
            <div
              key={index}
              className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer group"
              onClick={() => {
                // Handle Regal main app selection
                if (app.name === 'Regal (main view)') {
                  onAppSelect({ name: 'home', description: 'Main CRM view', icon: '🏠', color: 'bg-gray-600' });
                } else {
                  onAppSelect(app);
                }
                onClose();
              }}
            >
              <div className={`w-10 h-10 ${app.color} rounded-lg flex items-center justify-center text-white text-lg mr-3`}>
                {app.icon}
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">{app.name}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">{app.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppLauncher;